import { API_BASE } from './apiBase';

/**
 * The @font-face rules a card is actually wearing, with the files inlined — and nothing else.
 *
 * TWO problems, both of which show up as a card coming back in the wrong font.
 *
 * SIZE. html-to-image embeds every face it can find. The composer loads all seventeen display
 * families the moment it opens (`ensureAllDisplayFonts`), Google serves each split by unicode
 * subset, and Bagel Fat One alone ships ninety-odd — 232 rules, about 2.4MB of base64 inside a
 * single `data:image/svg+xml` URL. Desktop shrugs that off; a phone decodes what it can and
 * drops the rest. So: only the families on this card, only the subsets its characters need.
 *
 * ACCESS. Reading those rules at all means `sheet.cssRules`, which a cross-origin stylesheet
 * only gives up when it was fetched with CORS — and whether it was depends on how the browser
 * happened to cache it. That is why the same Portal came back right for one person and in a
 * fallback cursive for the next. So every step has a way through our own origin: the rules,
 * then the stylesheet as text, then the font files, each falling back to the API's proxy.
 */
const CACHE = new Map();

const clean = (s) => (s || '').trim().replace(/^["']|["']$/g, '').toLowerCase();
const proxied = (url) => `${API_BASE}/events/proxy-image?url=${encodeURIComponent(url)}`;

/** Ours first, then the same thing through the API — which is same-origin by definition. */
async function fetchEitherWay(url, as = 'text') {
  const read = async (u) => {
    const res = await fetch(u);
    if (!res.ok) throw new Error(String(res.status));
    return as === 'text' ? res.text() : res.blob();
  };
  try { return await read(url); } catch { return read(proxied(url)); }
}

/** The @font-face blocks of a stylesheet we could only get as text. */
export function facesInCss(text) {
  return [...(text || '').matchAll(/@font-face\s*{[^}]*}/g)].map((m) => m[0]);
}

const declOf = (css, prop) => {
  const m = css.match(new RegExp(`${prop}\\s*:\\s*([^;}]+)`, 'i'));
  return m ? m[1].trim() : '';
};

/**
 * Does this face's unicode-range cover anything the card actually says?
 *
 * Guessing by subset name doesn't work: Bagel Fat One is Japanese-capable and ships ninety-odd
 * subsets, most of which include a slice of ASCII, so "keep the latin ones" kept ninety. The
 * characters on the card are right there to check against.
 */
export function covers(range, used) {
  if (!range) return true;                                    // a face that doesn't say
  return range.split(',').some((token) => {
    const t = token.trim().replace(/^u\+/i, '');
    if (!t) return false;
    if (t.includes('?')) {                                    // U+30?? — a wildcard block
      const lo = parseInt(t.replace(/\?/g, '0'), 16);
      const hi = parseInt(t.replace(/\?/g, 'F'), 16);
      return [...used].some((c) => c >= lo && c <= hi);
    }
    const [a, b] = t.split('-').map((x) => parseInt(x, 16));
    if (Number.isNaN(a)) return false;
    const hi = Number.isNaN(b) ? a : b;
    return [...used].some((c) => c >= a && c <= hi);
  });
}

/** Every code point the card puts on screen. */
export function charsIn(node) {
  const used = new Set();
  for (const ch of (node.textContent || '')) used.add(ch.codePointAt(0));
  used.add(32);
  return used;
}

/** Every family named anywhere in the subtree, including the fallbacks after each comma. */
export function familiesIn(node) {
  const out = new Set();
  const take = (el) => {
    const ff = window.getComputedStyle(el).fontFamily;
    if (ff) ff.split(',').forEach((f) => out.add(clean(f)));
  };
  take(node);
  node.querySelectorAll('*').forEach(take);
  return out;
}

/** Same-origin copy of a font file, as a data: URL. */
async function inlineUrl(url) {
  if (CACHE.has(url)) return CACHE.get(url);
  const p = (async () => {
    const blob = await fetchEitherWay(url, 'blob');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('unreadable'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  })();
  CACHE.set(url, p);
  return p;
}

/**
 * `fontEmbedCSS` for html-to-image: the faces this card needs and no others.
 *
 * An empty answer sends html-to-image back to its own scan — a heavy card beats a wrong one.
 */
export default async function cardFontCss(node) {
  const wanted = familiesIn(node);
  const used = charsIn(node);
  const keep = (family, range) => wanted.has(clean(family)) && covers(range, used);

  const found = await Promise.all(Array.from(document.styleSheets).map(async (sheet) => {
    let rules = null;
    try { rules = Array.from(sheet.cssRules || []); } catch { rules = null; }   // opaque cross-origin
    if (rules) {
      return rules
        .filter((r) => r.type === 5 && keep(r.style.getPropertyValue('font-family'), r.style.getPropertyValue('unicode-range')))
        .map((r) => r.cssText);
    }
    // Unreadable: fetch the stylesheet itself, ours or through the proxy, and read the text.
    if (!sheet.href) return [];
    try {
      const text = await fetchEitherWay(sheet.href, 'text');
      return facesInCss(text).filter((css) => keep(declOf(css, 'font-family'), declOf(css, 'unicode-range')));
    } catch { return []; }
  }));

  const out = await Promise.all(found.flat().map(async (css) => {
    const urls = [...css.matchAll(/url\(["']?([^"')]+)["']?\)/g)].map((m) => m[1]);
    let done = css;
    await Promise.all(urls.map(async (u) => {
      if (/^data:/.test(u)) return;
      try {
        const data = await inlineUrl(new URL(u, document.baseURI).href);
        done = done.split(u).join(data);
      } catch {
        done = '';   // a face we can't carry is worse than no face: it would load nothing
      }
    }));
    return done;
  }));

  return out.filter(Boolean).join('\n');
}
